import {
  inputClasses,
  autocompleteClasses,
  Autocomplete,
  Divider,
  InputAdornment,
  TextField,
  OutlinedInput,
  Paper,
  Typography,
  createFilterOptions,
  styled,
} from "@mui/material";
import ReactFormatInput from "input-format/react";
import parsePhoneNumber from "libphonenumber-js";
import { rem } from "polished";
import React, { useRef, useState } from "react";

import { ariaLabel, CountryCodeOption, CountryCodeOptions } from "src/constants";
import { useFieldContext } from "src/hooks";

import { FormControl } from "../FormControl";
import { parse, format } from "./_utils";

const StyledInputAdornment = styled(InputAdornment)`
  position: relative;
  height: auto;
  max-height: none;
  padding-right: ${rem(4)};
`;

const StyledFlagImg = styled("img")`
  width: ${rem(24)};
  margin-right: ${(props) => props.theme.spacing(2)};
`;

const StyledPaper = styled(Paper)`
  width: ${rem(250)};
  margin-left: ${rem(-45)};
`;

const StyledTextField = styled(TextField)`
  && .${autocompleteClasses.input} {
    min-width: 25px;
  }
  .${inputClasses.underline}:before {
    border: none;
  }
  .${inputClasses.underline}:hover:not(.Mui-disabled):before {
    border: none;
  }
  .${inputClasses.underline}:after {
    border: none;
  }
`;

const StyledDivider = styled(Divider)`
  position: absolute;
  top: 0;
  right: 0;
`;

const DefaultCountryCodeOption: CountryCodeOption =
  CountryCodeOptions.find((phoneOption) => phoneOption.countryCode === "FR") ||
  CountryCodeOptions[0];

const filterOptions = createFilterOptions({
  stringify: (option: CountryCodeOption) => option.name.fra,
});

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const CustomFormatInput = React.forwardRef(function CustomFormatInput(props: any, ref) {
  const { inputRef, onChangeValue, ...rest } = props;

  return <ReactFormatInput {...rest} ref={inputRef} onChange={onChangeValue} />;
});

type Props = {
  id?: string;
};

export const PhoneField = ({ id }: Props) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const field = useFieldContext<string>();

  const [focused, setFocused] = useState(false);
  const [countryCodeOption, setCountryCodeOption] = useState<CountryCodeOption>(() => {
    const phoneNumber = parsePhoneNumber(field.state.value);
    return (
      CountryCodeOptions.find((phoneOption) => phoneOption.countryCode === phoneNumber?.country) ||
      DefaultCountryCodeOption
    );
  });

  const onChangeCountryCode = (_event: React.ChangeEvent<{}>, value?: CountryCodeOption | null) => {
    setCountryCodeOption(value || DefaultCountryCodeOption);
    field.handleChange("");
    inputRef.current?.focus();
  };

  return (
    <FormControl>
      <OutlinedInput
        name={field.name}
        value={field.state.value}
        className={focused ? "Mui-focused" : undefined}
        inputComponent={CustomFormatInput as any}
        autoComplete="tel-national"
        inputProps={{
          inputRef,
          onChangeValue: (value: string) => {
            const phoneNumber = parsePhoneNumber(value, countryCodeOption.countryCode);

            field.handleChange(phoneNumber?.number.toString() || value.trim());
          },
          parse,
          format: (value: string) => format(value, countryCodeOption.countryCode),
          id: id || field.name,
        }}
        startAdornment={
          <StyledInputAdornment position="start">
            <StyledFlagImg src={countryCodeOption.flag} alt={countryCodeOption.countryCode} />
            <Autocomplete
              openText={ariaLabel.openCountryList}
              closeText={ariaLabel.closeCountryList}
              disableClearable
              noOptionsText={ariaLabel.noResults}
              filterOptions={filterOptions}
              getOptionLabel={(option) => option.countryCode}
              renderOption={(props, option) => (
                <li {...props}>
                  <StyledFlagImg src={option.flag} alt={option.countryCode} />
                  <Typography>
                    {option.name.fra} (+{option.callingCode})
                  </Typography>
                </li>
              )}
              slots={{
                paper: StyledPaper,
              }}
              value={countryCodeOption}
              onChange={onChangeCountryCode}
              options={CountryCodeOptions}
              renderInput={(params) => (
                <StyledTextField
                  {...params}
                  placeholder="Pays"
                  variant="standard"
                  onFocus={() => setFocused(true)}
                  onBlur={() => setFocused(false)}
                />
              )}
            />
            <StyledDivider orientation="vertical" />
          </StyledInputAdornment>
        }
      />
    </FormControl>
  );
};
